import React, {useState, useEffect, useCallback} from 'react';
import './Devices.scss';
import 'moment/locale/ru';
import Tab from "rsuite/Tab";
import {Tabs, Input, InputGroup} from "rsuite";
import DevicesTable from "../../components/DevicesTable/DevicesTable";
import MobileDevicesTable from "../../components/DevicesTable/MobileDevicesTable";
import Loader from "../../components/shared/Loader/Loader";
import {AiOutlineSearch} from "react-icons/ai";
import {useNavigate} from "react-router-dom";
import { SharedApi } from "../../services/Shared.api";
import { ExternalDevicesUtils } from "../../components/menu/ExternalDevices/ExternalDevices.utils";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [devicesFullData, setDevicesFullData] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [newDevices, setNewDevices] = useState([]);
  const [onlineDevices, setOnlineDevices] = useState([]);
  const [offlineDevices, setOfflineDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingIcons, setLoadingIcons] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin]= useState(false)
  const [activeTab, setActiveTab] = useState('all');
  const [isMobileTable, setIsMobileTable] = useState(() => window.innerWidth < 600);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobileTable(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setIsAdmin(user?.isAdmin)
  }, []);

  useEffect(() => {
    if (!isAdmin && devices.length > 0) {
      const firstDeviceId = devices[0]?.id;
      navigate(`/devices/${firstDeviceId}`);
    }
  }, [isAdmin, devices, navigate]);

  useEffect(() => {
    const newDevicesList = devicesFullData.filter(device => device?.isNew === true);
    setNewDevices(newDevicesList);
  }, [devicesFullData]);

  const getDevicesStatusBatch = useCallback(async (deviceGuids) => {
    try {
      if (!deviceGuids || deviceGuids?.length === 0) {
        return [];
      }

      return await SharedApi.getControlBlocksStatusBatch(deviceGuids);
    } catch (error) {
      console.error('Error fetching devices status batch:', error);
      return [];
    }
  }, []);

  const loadBasicDevicesInfo = useCallback(async () => {
    try {
      return await SharedApi.getControlBlocks();
    } catch (error) {
      console.error('Error fetching devices:', error);
      return [];
    }
  }, []);

  const loadDevicesIcons = useCallback(async (devicesList) => {
    try {
      if (!devicesList || devicesList.length === 0) {
        return devicesList;
      }

      const deviceGuids = devicesList.map(device => device.id).filter(id => id);
      const statuses = await getDevicesStatusBatch(deviceGuids);
      const statusMap = new Map();

      statuses.forEach(status => {
        statusMap.set(status.id, status);
      });

      return devicesList.map(device => {
        const deviceStatus = statusMap.get(device.id);
        return {
          ...device,
          isConnected: deviceStatus?.isConnected || false,
          icons: {
            hasSimCard: deviceStatus?.status?.hasSimCard || false,
            hasBigLineDevices: deviceStatus?.status?.hasBigLineDevices || false,
            hasUsers: deviceStatus?.status?.hasUsers || false,
            isEthernetConnected: deviceStatus?.status?.isEthernetConnected || false,
            isWiFiConnected: deviceStatus?.status?.isWiFiConnected || false,
            environment: deviceStatus?.status?.environment || ''
          }
        };
      });
    } catch (error) {
      console.error('Error loading devices icons:', error);
      return devicesList;
    }
  }, [getDevicesStatusBatch]);

  const mapBigLineDevices = useCallback((stations) => (
    stations.map(station => ({
      ...station,
      bigLineDevices: (station?.devices ?? []).map(device => ({
        text: ExternalDevicesUtils.formatDeviceSerialNumber(
          device.serialNumber,
          device.type,
          device.model
        ),
        isConnected: device.isConnected ?? false,
      })),
    }))
  ), []);

  const filterDevices = useCallback((devices, searchText) => {
    if (!searchText.trim()) {
      return devices;
    }

    const searchLower = searchText.toLowerCase();
    return devices.filter(device =>
      device?.clockStation?.model?.toLowerCase().includes(searchLower) ||
      device?.clockStation?.firmwareVersion?.toLowerCase().includes(searchLower) ||
      device?.model?.toLowerCase().includes(searchLower) ||
      device?.article?.toLowerCase().includes(searchLower) ||
      device?.controlBlockFirmwareVersion?.toLowerCase().includes(searchLower) ||
      device.clockStation?.serialNumber?.toLowerCase().includes(searchLower) ||
      device.clockStation?.clockSerialNumber?.toLowerCase().includes(searchLower) ||
      device.clockStation?.clockSystemSerialNumber?.toLowerCase().includes(searchLower) ||
      device.clockStation?.clockChannels?.some(channel =>
        channel?.serialNumber?.toLowerCase().includes(searchLower)
      ) ||
      device?.bigLineDevices?.some(bigLineDevice =>
        bigLineDevice.text.toLowerCase().includes(searchLower)
      ) ||
      device?.serialNumber?.toLowerCase().includes(searchLower) ||
      device?.clockSerialNumber?.toLowerCase().includes(searchLower) ||
      device?.clockSystemSerialNumber?.toLowerCase().includes(searchLower) ||
      device.description?.toLowerCase().includes(searchLower) ||
      device.privateDescription?.toLowerCase().includes(searchLower)
    );
  }, []);

  const populateDevicesData = useCallback(async () => {
    try {
      setLoading(true);

      const basicDevicesData = await loadBasicDevicesInfo();
      setDevices(basicDevicesData);
      setDevicesFullData(basicDevicesData);
      setFilteredDevices(basicDevicesData);

      setLoading(false);

      if (basicDevicesData.length > 0) {
        setLoadingIcons(true);
        const devicesWithIcons = await loadDevicesIcons(basicDevicesData);
        setDevicesFullData(devicesWithIcons);
        setFilteredDevices(devicesWithIcons);

        const devicesWithBigLine = mapBigLineDevices(devicesWithIcons);
        setDevicesFullData(devicesWithBigLine);
        setFilteredDevices(devicesWithBigLine);

        const online = devicesWithBigLine.filter(d => d.isConnected);
        const offline = devicesWithBigLine.filter(d => !d.isConnected);

        setOnlineDevices(online);
        setOfflineDevices(offline);

        setLoadingIcons(false);
      }

    } catch (error) {
      console.error('Error in populateDevicesData:', error);
      setLoading(false);
      setLoadingIcons(false);
    }
  }, [loadBasicDevicesInfo, loadDevicesIcons, mapBigLineDevices]);

  const handleSearch = useCallback(() => {
    const filtered = filterDevices(devicesFullData, searchTerm);
    setFilteredDevices(filtered);
  }, [devicesFullData, searchTerm, filterDevices]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setFilteredDevices(devicesFullData);
  }, [devicesFullData]);

  useEffect(() => {
    let devicesToFilter = devicesFullData;

    if (activeTab === 'new') {
      devicesToFilter = newDevices;
    }

    if (activeTab === 'online') {
      devicesToFilter = onlineDevices;
    }

    if (activeTab === 'offline') {
      devicesToFilter = offlineDevices;
    }

    const filtered = filterDevices(devicesToFilter, searchTerm);

    setFilteredDevices(filtered);
    return undefined;
  }, [
    activeTab,
    devicesFullData,
    filterDevices,
    newDevices,
    offlineDevices,
    onlineDevices,
    searchTerm,
  ]);

  useEffect(() => {
    populateDevicesData();
  }, [populateDevicesData]);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    setSearchTerm('');
  }, []);

  const renderMobileTabTitle = (label, count) => (
    <span className="devices-mobile-tab-title">
      <span>{label} </span>
      <span className="devices-mobile-tab-title__count">{count}</span>
    </span>
  );

  const renderAllDevicesTable = () => {
    return (
      <div className="devices-page__table-viewport">
        {isMobileTable ? (
          <MobileDevicesTable
            devices={filteredDevices}
            loading={loading}
            loadingIcons={loadingIcons}
            highlightNew={activeTab === 'all'}
          />
        ) : (
          <DevicesTable
            devices={filteredDevices}
            loading={loading}
            loadingIcons={loadingIcons}
            highlightNew={activeTab === 'all'}
          />
        )}
      </div>
    );
  };

  const renderNewDevicesTable = () => {
    return (
      <div className="devices-page__table-viewport">
        {isMobileTable ? (
          <MobileDevicesTable
            devices={filteredDevices}
            loading={loading}
            loadingIcons={loadingIcons}
            highlightNew={false}
          />
        ) : (
          <DevicesTable
            devices={filteredDevices}
            loading={loading}
            loadingIcons={loadingIcons}
            highlightNew={false}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loader-wrapper"><Loader/></div>;
  }

  return (
    <div className="devices-page">
      {isMobileTable && (
        <div className="devices-mobile-search mt-2 mb-2">
          <InputGroup>
            <Input
              placeholder="Поиск станции или устройства..."
              value={searchTerm}
              onChange={setSearchTerm}
              onPressEnter={handleSearch}
            />

            {!searchTerm && (
              <InputGroup.Button onClick={handleSearch}>
                <AiOutlineSearch />
              </InputGroup.Button>
            )}

            {searchTerm && (
              <InputGroup.Button onClick={handleClearSearch}>
                ×
              </InputGroup.Button>
            )}
          </InputGroup>
        </div>
      )}

      <div className={`search-panel mb-3 mt-2 ${isMobileTable ? "search-panel--mobile" : ""}`}>
        <div className="devices-search-panel__row">
          <div className={`search-panel__search-col ${isMobileTable ? "search-panel__input-col--mobile" : ""}`}>
            <InputGroup>
              <Input
                placeholder="Поиск станции или устройства..."
                value={searchTerm}
                onChange={setSearchTerm}
                onPressEnter={handleSearch}
              />

              {!searchTerm && (
                <InputGroup.Button onClick={handleSearch}>
                  <AiOutlineSearch />
                </InputGroup.Button>
              )}

              {searchTerm && (
                <InputGroup.Button onClick={handleClearSearch}>
                  ×
                </InputGroup.Button>
              )}
            </InputGroup>
          </div>
          <div className={`search-panel__meta text-md-end ${isMobileTable ? "search-panel__meta--mobile-hidden" : ""}`}>
            <span className={`text-muted ${isMobileTable ? "devices-search-count--mobile-hidden" : ""}`}>
              Найдено: {filteredDevices.length} из{' '}
              {activeTab === 'new' ? newDevices.length : devicesFullData.length} станций
              {loadingIcons && ' (загрузка статусов...)'}
              {activeTab === 'new' && ` (${newDevices.length} новых)`}
            </span>
          </div>
        </div>
      </div>

      <Tabs
        className="mt-2 devices-page__tabs"
        defaultActiveKey="all"
        id="device-tabs"
        activeKey={activeTab}
        onSelect={handleTabChange}
      >
        <Tab eventKey="all" title={isMobileTable ? "Все" : "Все устройства"}>
          {renderAllDevicesTable()}
        </Tab>
        <Tab
          eventKey="new"
          title={isMobileTable ? renderMobileTabTitle("Новые", newDevices.length) : `Новые устройства (${newDevices.length})`}
        >
          {renderNewDevicesTable()}
        </Tab>
        <Tab
          eventKey="online"
          title={isMobileTable ? renderMobileTabTitle("В сети", onlineDevices.length) : `В сети (${onlineDevices.length})`}
        >
          {renderNewDevicesTable()}
        </Tab>
        <Tab
          eventKey="offline"
          title={isMobileTable ? renderMobileTabTitle("Не в сети", offlineDevices.length) : `Не в сети (${offlineDevices.length})`}
        >
          {renderNewDevicesTable()}
        </Tab>

        {/*<Tab eventKey="main" title="Основные">*/}
        {/*  <p>Основные</p>*/}
        {/*</Tab>*/}
        {/*<Tab eventKey="favorite" title="Избранные">*/}
        {/*  <p>Избранные</p>*/}
        {/*</Tab>*/}
        {/*<Tab eventKey="track" title="Отслеживаемые">*/}
        {/*  <p>Отслеживаемые</p>*/}
        {/*</Tab>*/}
        {/*<Tab eventKey="arhive" title="Архивные">*/}
        {/*  <p>Архивные</p>*/}
        {/*</Tab>*/}
        {/*<Tab eventKey="test" title="Тестовые">*/}
        {/*  <p>Тестовые</p>*/}
        {/*</Tab>*/}
      </Tabs>
    </div>
  );
};

export default Devices;
